import classNames from 'classnames'
import * as React from 'react'
import {useQueryLoader} from 'react-relay'

import {useAppSelector} from '../../../../hooks/react-redux'
import {useBuildStatusIcon} from '../../../../hooks/useBuildStatusIcon'
import {BuildStatusType, stringifyId} from '../../../../types'
import {isWaitReasonWithWarning, isWaitReasonWithAnimation} from '../../../../utils/waitReason'
import {formatBuildNumber} from '../../BuildNumber/BuildNumber.utils'
import DotsLoader from '../../DotsLoader/DotsLoader'
import LinkWithIcon from '../../LinkWithIcon/LinkWithIcon'
import RouterLinkWrapper from '../../RouterLinkWrapper/RouterLinkWrapper'

import type {Props} from './BuildStatusLink.types'
import {useTitle} from './BuildStatusLink.utils'
import type {BuildPersonalChangeCommentQuery} from './BuildStatusTooltip/BuildPersonalChangeComment/__generated__/BuildPersonalChangeCommentQuery.graphql'
import {personalChangeCommentQuery} from './BuildStatusTooltip/BuildPersonalChangeComment/BuildPersonalChangeComment'
import BuildStatusTooltip from './BuildStatusTooltip/BuildStatusTooltip.container'
import {getPersonalChangeId} from './BuildStatusTooltip/BuildStatusTooltip.selectors'

import styles from './BuildStatusLink.css'

export default function BuildStatusLink(props: Props) {
  const {
    className,
    buildId,
    buildUrl,
    statusType,
    isSakuraUI = false,
    useNumber,
    number,
    isQueued = false,
    queuePosition,
    waitReason,
    withIcon = true,
    noLink,
  } = props
  const icon = useBuildStatusIcon(buildId)
  const title = useTitle(props)
  const text =
    useNumber === true
      ? formatBuildNumber({
          number,
          isQueued,
          queuePosition,
        }).text
      : title
  const personalChangeId = useAppSelector(state => getPersonalChangeId(state, buildId))
  const [
    personalChangeCommentQueryRef,
    loadPersonalChangeCommentQuery,
    disposePersonalChangeCommentQuery,
  ] = useQueryLoader<BuildPersonalChangeCommentQuery>(personalChangeCommentQuery)
  const iconTitle = React.useMemo(
    () => (
      <BuildStatusTooltip
        buildId={buildId}
        personalChangeCommentQueryRef={personalChangeCommentQueryRef}
      />
    ),
    [buildId, personalChangeCommentQueryRef],
  )
  const linkClassName = classNames(className, styles.link, {
    [styles.success]: isSakuraUI && statusType === BuildStatusType.SUCCESS,
    [styles.failure]: isSakuraUI && statusType === BuildStatusType.FAILURE,
    [styles.canceled]: isSakuraUI && statusType === BuildStatusType.CANCELED,
    [styles.warning]: isQueued && isWaitReasonWithWarning(waitReason),
  })
  return text != null && text.length > 0 ? (
    <RouterLinkWrapper>
      <LinkWithIcon
        tailLength={0}
        icon={withIcon ? icon : null}
        iconTitle={withIcon ? iconTitle : null}
        href={buildUrl}
        className={linkClassName}
        textTitle={title}
        rightNodes={isQueued && isWaitReasonWithAnimation(waitReason) ? <DotsLoader /> : undefined}
        noLink={noLink}
        onShowIconTitle={() =>
          personalChangeId != null &&
          loadPersonalChangeCommentQuery({
            changeLocator: `id:${stringifyId(personalChangeId)},personal:true`,
          })
        }
        onHideIconTitle={disposePersonalChangeCommentQuery}
      >
        {text}
      </LinkWithIcon>
    </RouterLinkWrapper>
  ) : null
}
